import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@repletoryreach/sdk-auth';
import { useRealtime } from '@repletoryreach/sdk-realtime';
import { ThemeToggle } from '@repletoryreach/sdk-ui';
import { Burger } from '@repletoryreach/sdk-ui/nav';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { useT } from '../../i18n/I18nProvider';
import { ProfileMenu } from '../components/ProfileMenu';
import { NAV, activeNavPath } from '../../lib/nav';

export interface HeaderBarProps {
  /** ≤768px layout; shows the burger that opens the off-canvas nav. */
  mobile?: boolean;
  /** Off-canvas nav state (mobile only); drives the burger's X state. */
  menuOpened?: boolean;
  /** Opens the off-canvas nav (mobile only). */
  onMenuClick?: () => void;
}

/** Realtime connection states we render. Anything else falls back to disconnected copy. */
const RT_STATES = ['connected', 'connecting', 'disconnected'] as const;
type RtState = (typeof RT_STATES)[number];

export function HeaderBar({ mobile = false, menuOpened = false, onMenuClick }: HeaderBarProps = {}) {
  const { user } = useAuth();
  const { status } = useRealtime();
  const { pathname } = useLocation();
  const t = useT();

  // The same rule as the menu, so the title and the highlighted item always agree.
  const items = NAV.flatMap((s) => s.items);
  const activePath = activeNavPath(pathname, items.map((i) => i.path));
  const active = items.find((i) => i.path === activePath);
  const sectionTitle = active ? t(active.labelKey, active.label) : '';

  const rt: RtState = (RT_STATES as readonly string[]).includes(status)
    ? (status as RtState)
    : 'disconnected';

  return (
    <header className="rr-shell__header">
      <div className="rr-shell__header-left">
        {mobile && (
          <Burger
            className="rr-shell__burger"
            size="sm"
            opened={menuOpened}
            onClick={onMenuClick}
            label={menuOpened ? t('nav.closeMenu') : t('nav.openMenu')}
          />
        )}

        {/*
          Was an 8px unlabelled dot whose only affordance was
          title="realtime: disconnected": developer-speak, invisible to screen
          readers, and no explanation of the consequence
          (_UI_improvements/tenant/_app-shell.md §4 item 10). Now a labelled
          status with a tooltip that says what it means for the user.
        */}
        <Tooltip label={t(`shell.realtime.tooltip.${rt}`)} position="bottom">
          <span className={`rr-rt-status rr-rt-status--${rt}`} role="status">
            <span className={`rr-rt-indicator rr-rt-indicator--${rt}`} aria-hidden />
            <span className="rr-rt-status__label">{t(`shell.realtime.${rt}`)}</span>
          </span>
        </Tooltip>

        {/* The page's own name, so the header says where you are even when the
            rail is collapsed behind a burger. Taken from the nav config, which
            is already the single source of truth for these labels. */}
        {sectionTitle && <span className="rr-shell__page-title">{sectionTitle}</span>}

        <span className="rr-shell__header-sep" aria-hidden />

        {/*
          The org used to be plain text. It is the account everything on screen
          belongs to, so it now reads as an identity chip that goes to the
          organisation settings; the reference draws it as a switcher, and this
          is the honest version of that while a user belongs to exactly one org.
        */}
        <Link to="/settings/organisation" className="rr-shell__org" title={t('shell.org.tooltip')}>
          <OrgIcon />
          <span className="rr-shell__org-name">{user?.org.name}</span>
        </Link>
      </div>

      <div className="rr-shell__header-right">
        {/* Theme had no shell control at all; it was reachable only through
            Settings → Appearance. Admin-ui puts it here; parity now. */}
        <ThemeToggle iconOnly size="xs" />
        <ProfileMenu />
      </div>
    </header>
  );
}

function OrgIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2.5 14V3.2a.7.7 0 01.7-.7h5.6a.7.7 0 01.7.7V14" />
      <path d="M9.5 14V7h3.8a.7.7 0 01.7.7V14M1.5 14h13" />
      <path d="M5 5.5h1.5M5 8h1.5M5 10.5h1.5" />
    </svg>
  );
}
